// eslint-disable-next-line no-restricted-imports
import React, { useEffect } from "react";

import { RemixBrowser, useLocation, useMatches } from "@remix-run/react";
import { hydrateRoot } from "react-dom/client";

import { UPDATE_AVAILABLE_EVENT, safeReload } from "~/lib/app-update";
import { initConsoleBridge } from "~/lib/dev/console-bridge";
import { setupSentry } from "~/lib/sentry/client";

// AIDEV-NOTE: Initialize console bridge early to capture all browser logs in server output.
// Must be before setupSentry and hydration to catch early errors.
if (import.meta.env.DEV) {
    initConsoleBridge();
}

// AIDEV-NOTE: Reactive stale-PWA recovery. Vite fires `vite:preloadError` on any
// failed dynamic import — which is exactly what happens when a client running an
// old build lazy-loads a code-split chunk whose hash a newer deploy rotated away
// (404). preventDefault() stops Vite from rethrowing, then we reload onto the
// fresh build. safeReload() is a no-op while a recording/upload is in flight; in
// that case we surface the proactive prompt instead so the user picks the moment.
// PROD-only to avoid interfering with dev HMR. See app-update.ts.
if (import.meta.env.PROD && typeof window !== "undefined") {
    window.addEventListener("vite:preloadError", (event) => {
        event.preventDefault();
        if (!safeReload()) {
            window.dispatchEvent(new Event(UPDATE_AVAILABLE_EVENT));
        }
    });
}

setupSentry();

// AIDEV-NOTE: Safari < 15.4 lacks crypto.randomUUID; provide a tiny fallback
if (
    typeof window !== "undefined" &&
    typeof window.crypto !== "undefined" &&
    typeof window.crypto.randomUUID !== "function"
) {
    window.crypto.randomUUID = () => {
        const bytes = new Uint8Array(16);
        window.crypto.getRandomValues(bytes);
        // Per RFC4122 version 4
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;
        const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
        return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex
            .slice(6, 8)
            .join("")}-${hex.slice(8, 10).join("")}-${hex
            .slice(10, 16)
            .join("")}`;
    };
}

function hydrate() {
    // AIDEV-NOTE: Hydration diagnostics were removed in cleanup
    let preHydrationHTML: string | null = null;

    // Set up mutation observer to catch DOM changes during hydration (debug only)
    const mutations: MutationRecord[] = [];

    let observer: MutationObserver | null = null;
    if (__HYDRATION_DEBUG__) {
        observer = new MutationObserver((mutationList) => {
            mutations.push(...mutationList);
        });
        observer.observe(document.documentElement, {
            childList: true,
            attributes: true,
            characterData: true,
            subtree: true,
            attributeOldValue: true,
            characterDataOldValue: true,
        });
    }

    // Track hydration errors in detail (stored in-memory; only logged in debug)
    const hydrationErrors: Array<{
        error: unknown;
        errorInfo: unknown;
        timestamp: number;
    }> = [];

    React.startTransition(() => {
        hydrateRoot(
            document,
            <React.StrictMode>
                <RemixBrowser />
            </React.StrictMode>,
            {
                onRecoverableError: (error, errorInfo) => {
                    // Ignore recoverable hydration errors that originate from the <head> section.
                    // We identify those by checking common head-level elements in the component stack.
                    const isHeadMismatch =
                        /\b(head|title|meta|link|style)\b/i.test(
                            errorInfo.componentStack ?? "",
                        );
                    if (isHeadMismatch) {
                        return; // Silently ignore
                    }

                    const errorData = {
                        error,
                        errorInfo,
                        timestamp: Date.now(),
                    };
                    hydrationErrors.push(errorData);
                    // AIDEV-NOTE: Do not log hydration error details in production
                },
            },
        );
    });

    // Log results after hydration (debug only)
    setTimeout(() => {
        if (observer) observer.disconnect();
    }, 500);
}

hydrate();
